import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { fetchDashboardStats, fetchRecentActivity } from '../store/slices/dashboardSlice';
import { RootState, AppDispatch } from '../store';
import { UsersIcon, ShoppingBagIcon, DocumentTextIcon, ClipboardDocumentListIcon, TagIcon, PhotoIcon } from '@heroicons/react/24/outline';

const Dashboard: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const { stats, recentActivity, isLoading, error } = useSelector((state: RootState) => state.dashboard);

  useEffect(() => {
    console.log('Fetching dashboard stats...');
    dispatch(fetchDashboardStats());
    dispatch(fetchRecentActivity());
  }, [dispatch]);

  useEffect(() => {
    console.log('Dashboard stats:', stats);
  }, [stats]);

  const statsData = [
    { name: 'Total Users', value: stats.totalUsers.toString(), icon: UsersIcon, color: 'bg-gradient-to-r from-blue-500 to-blue-600' },
    { name: 'Total Products', value: stats.totalProducts.toString(), icon: ShoppingBagIcon, color: 'bg-gradient-to-r from-green-500 to-green-600' },
    { name: 'Total Orders', value: stats.totalOrders.toString(), icon: ClipboardDocumentListIcon, color: 'bg-gradient-to-r from-yellow-500 to-yellow-600' },
    { name: 'Total Blogs', value: stats.totalBlogs.toString(), icon: DocumentTextIcon, color: 'bg-gradient-to-r from-purple-500 to-purple-600' },
    { name: 'Total Categories', value: stats.totalCategories.toString(), icon: TagIcon, color: 'bg-gradient-to-r from-indigo-500 to-indigo-600' },
    { name: 'Total Banners', value: stats.totalBanners.toString(), icon: PhotoIcon, color: 'bg-gradient-to-r from-pink-500 to-pink-600' },
  ];

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="bg-white rounded-lg shadow-sm p-6">
        <h1 className="text-3xl font-bold text-gray-900">Dashboard</h1>
        <p className="mt-2 text-gray-600">Welcome to Rudraksh Admin Panel</p>
      </div>

      {/* Stats Grid */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
          <p className="text-red-600">Error loading dashboard data: {error}</p>
        </div>
      )}
      {isLoading ? (
        <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
          {[1,2,3,4,5,6].map((i) => (
            <div key={i} className="bg-white overflow-hidden shadow-lg rounded-xl animate-pulse">
              <div className="p-6">
                <div className="flex items-center justify-between">
                  <div className="flex items-center">
                    <div className="bg-gray-300 rounded-xl p-3 w-14 h-14"></div>
                    <div className="ml-4">
                      <div className="h-4 bg-gray-300 rounded w-20 mb-2"></div>
                      <div className="h-6 bg-gray-300 rounded w-16"></div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
          {statsData.map((stat) => (
            <div key={stat.name} className="bg-white overflow-hidden shadow-lg rounded-xl hover:shadow-xl transition-shadow duration-300">
              <div className="p-6">
                <div className="flex items-center justify-between">
                  <div className="flex items-center">
                    <div className={`${stat.color} rounded-xl p-3 shadow-lg`}>
                      <stat.icon className="h-8 w-8 text-white" />
                    </div>
                    <div className="ml-4">
                      <p className="text-sm font-medium text-gray-500">{stat.name}</p>
                      <p className="text-2xl font-bold text-gray-900">{stat.value}</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Content Grid */}
      <div className="grid grid-cols-1 gap-8 lg:grid-cols-2">
        {/* Recent Activity */}
        <div className="bg-white shadow-lg rounded-xl p-6">
          <h3 className="text-xl font-semibold text-gray-900 mb-6">Recent Activity</h3>
          <div className="space-y-4">
            {recentActivity.length > 0 ? (
              recentActivity.map((activity, index) => (
                <div key={index} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                  <div className="flex items-center space-x-3">
                    <div className={`w-3 h-3 ${activity.color} rounded-full`}></div>
                    <span className="text-sm font-medium text-gray-700">{activity.text}</span>
                  </div>
                  <span className="text-xs text-gray-500">{activity.time}</span>
                </div>
              ))
            ) : (
              <div className="text-center text-gray-500 py-4">
                No recent activity
              </div>
            )}
          </div>
        </div>

        {/* Quick Actions */}
        <div className="bg-white shadow-lg rounded-xl p-6">
          <h3 className="text-xl font-semibold text-gray-900 mb-6">Quick Actions</h3>
          <div className="space-y-3">
            {[
              { text: 'Add New Product', color: 'blue', icon: '🛍️', route: '/products' },
              { text: 'Create Blog Post', color: 'green', icon: '📝', route: '/blogs' },
              { text: 'Add Category', color: 'purple', icon: '🏷️', route: '/categories' },
              { text: 'Manage Orders', color: 'yellow', icon: '📊', route: '/orders' },
            ].map((action, index) => (
              <button 
                key={index} 
                onClick={() => navigate(action.route)}
                className={`w-full flex items-center justify-between p-4 bg-${action.color}-50 hover:bg-${action.color}-100 rounded-lg text-${action.color}-700 transition-colors duration-200 group`}
              >
                <div className="flex items-center space-x-3">
                  <span className="text-lg">{action.icon}</span>
                  <span className="font-medium">{action.text}</span>
                </div>
                <svg className="w-5 h-5 group-hover:translate-x-1 transition-transform duration-200" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </button>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;